/**
 * Create and inspect GraphQL type definitions and schemas.
 *
 * These exports are also available from the root `graphql` package.
 * @packageDocumentation
 */

export type { Path as ResponsePath } from '../jsutils/Path.ts';

export {
  // Predicate
  isSchema,
  // Assertion
  assertSchema,
  // GraphQL Schema definition
  GraphQLSchema,
} from './schema.ts';
export type { GraphQLSchemaConfig, GraphQLSchemaExtensions } from './schema.ts';

export type {
  GraphQLField,
  GraphQLArgument,
  GraphQLEnumValue,
  GraphQLInputField,
} from './definition.ts';
export {
  resolveObjMapThunk,
  resolveReadonlyArrayThunk,
  // Predicates
  isType,
  isScalarType,
  isObjectType,
  isField,
  isArgument,
  isInterfaceType,
  isUnionType,
  isEnumType,
  isEnumValue,
  isInputObjectType,
  isListType,
  isNonNullType,
  isInputType,
  isInputField,
  isOutputType,
  isLeafType,
  isCompositeType,
  isAbstractType,
  isWrappingType,
  isNullableType,
  isNamedType,
  isRequiredArgument,
  isRequiredInputField,
  // Assertions
  assertType,
  assertScalarType,
  assertObjectType,
  assertField,
  assertArgument,
  assertInterfaceType,
  assertUnionType,
  assertEnumType,
  assertEnumValue,
  assertInputObjectType,
  assertInputField,
  assertListType,
  assertNonNullType,
  assertInputType,
  assertOutputType,
  assertLeafType,
  assertCompositeType,
  assertAbstractType,
  assertWrappingType,
  assertNullableType,
  assertNamedType,
  // Un-modifiers
  getNullableType,
  getNamedType,
  // Definitions
  GraphQLScalarType,
  GraphQLObjectType,
  GraphQLInterfaceType,
  GraphQLUnionType,
  GraphQLEnumType,
  GraphQLInputObjectType,
  // Type Wrappers
  GraphQLList,
  GraphQLNonNull,
} from './definition.ts';

export type {
  GraphQLType,
  GraphQLInputType,
  GraphQLOutputType,
  GraphQLLeafType,
  GraphQLCompositeType,
  GraphQLAbstractType,
  GraphQLWrappingType,
  GraphQLNullableType,
  GraphQLNullableInputType,
  GraphQLNullableOutputType,
  GraphQLNamedType,
  GraphQLNamedInputType,
  GraphQLNamedOutputType,
  ThunkReadonlyArray,
  ThunkObjMap,
  GraphQLArgumentConfig,
  GraphQLArgumentExtensions,
  GraphQLEnumTypeConfig,
  GraphQLEnumTypeExtensions,
  GraphQLEnumValueConfig,
  GraphQLEnumValueConfigMap,
  GraphQLEnumValueExtensions,
  GraphQLFieldConfig,
  GraphQLFieldConfigArgumentMap,
  GraphQLFieldConfigMap,
  GraphQLFieldExtensions,
  GraphQLFieldMap,
  GraphQLFieldResolver,
  GraphQLInputFieldConfig,
  GraphQLInputFieldConfigMap,
  GraphQLInputFieldExtensions,
  GraphQLInputFieldMap,
  GraphQLInputObjectTypeConfig,
  GraphQLInputObjectTypeExtensions,
  GraphQLInterfaceTypeConfig,
  GraphQLInterfaceTypeExtensions,
  GraphQLIsTypeOfFn,
  GraphQLObjectTypeConfig,
  GraphQLObjectTypeExtensions,
  GraphQLResolveInfo,
  GraphQLResolveInfoHelpers,
  GraphQLScalarTypeConfig,
  GraphQLScalarTypeExtensions,
  GraphQLTypeResolver,
  GraphQLUnionTypeConfig,
  GraphQLUnionTypeExtensions,
  GraphQLScalarSerializer,
  GraphQLScalarValueParser,
  GraphQLScalarLiteralParser,
  GraphQLScalarOutputValueCoercer,
  GraphQLScalarInputValueCoercer,
  GraphQLScalarInputLiteralCoercer,
  GraphQLDefaultInput,
} from './definition.ts';

export {
  // Predicate
  isDirective,
  // Assertion
  assertDirective,
  // Directives Definition
  GraphQLDirective,
  // Built-in Directives defined by the Spec
  isSpecifiedDirective,
  specifiedDirectives,
  GraphQLIncludeDirective,
  GraphQLSkipDirective,
  GraphQLDeferDirective,
  GraphQLStreamDirective,
  GraphQLDeprecatedDirective,
  GraphQLSpecifiedByDirective,
  GraphQLOneOfDirective,
  // Constant Deprecation Reason
  DEFAULT_DEPRECATION_REASON,
} from './directives.ts';

export type {
  GraphQLDirectiveConfig,
  GraphQLDirectiveExtensions,
} from './directives.ts';

// Common built-in scalar instances.
export {
  // Predicate
  isSpecifiedScalarType,
  // Standard GraphQL Scalars
  specifiedScalarTypes,
  GraphQLInt,
  GraphQLFloat,
  GraphQLString,
  GraphQLBoolean,
  GraphQLID,
  // Int boundaries constants
  GRAPHQL_MAX_INT,
  GRAPHQL_MIN_INT,
} from './scalars.ts';

export {
  // Predicate
  isIntrospectionType,
  // GraphQL Types for introspection.
  introspectionTypes,
  __Schema,
  __Directive,
  __DirectiveLocation,
  __Type,
  __Field,
  __InputValue,
  __EnumValue,
  __TypeKind,
  // "Enum" of Type Kinds
  TypeKind,
  // Meta-field definitions.
  SchemaMetaFieldDef,
  TypeMetaFieldDef,
  TypeNameMetaFieldDef,
} from './introspection.ts';

// Validate GraphQL schema.
export { validateSchema, assertValidSchema } from './validate.ts';

// Upholds the spec rules about naming.
export { assertName, assertEnumValueName } from './assertName.ts';
